import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, FileDown, Send, Trash2, Plus, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
    adminProposals,
    adminCreateProposal,
    adminSendProposal,
    adminDeleteProposal,
    adminProposalPdf,
    adminLinkProposal,
    adminClients,
} from "../../lib/api";

const fillPlaceholders = (html) => {
    let out = html.replace(/^\s*<!doctype[^>]*>/i, "");
    const firstTag = out.indexOf("<");
    if (firstTag > 0 && ["html", "html.", "copy", "code"].includes(out.slice(0, firstTag).trim().toLowerCase()))
        out = out.slice(firstTag);
    const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    return out
        .replace(/\[your name\s*\/\s*company name\]/gi, "Alexandra Hudson / Consulting WorX")
        .replace(/\[company name\]/gi, "Consulting WorX")
        .replace(/\[your name\]/gi, "Alexandra Hudson")
        .replace(/\[(insert )?date\]/gi, today);
};

const previewDoc = (form) => `<!doctype html><html><head><style>
body{font-family:Arial,sans-serif;color:#0A0A0A;padding:20px;font-size:14px;line-height:1.6;margin:0}
.masthead{background:#0A0A0A;color:#F4F4F0;padding:18px 22px;margin:-20px -20px 20px}
.brand{letter-spacing:3px;font-weight:bold;font-size:15px}
.rule{height:3px;background:#FF4500;width:56px;margin:10px 0 6px}
.small{font-size:10px;letter-spacing:1.5px;color:#F4F4F0}
h1,h2,h3{margin:0.5em 0}
.meta{color:#777;font-size:11px;margin:0 0 14px}
table{border-collapse:collapse;width:100%}td,th{padding:5px 8px;border-bottom:1px solid #e5e2da;text-align:left}
img{max-width:100%}
</style></head><body>
<div class="masthead"><div class="brand">CONSULTING WORX</div><div class="rule"></div><div class="small">PROPOSAL</div></div>
<h2 style="margin-top:0">${form.title || "Proposal title"}</h2>
<p class="meta">Prepared for ${form.client_name || "Client"} by Alexandra Hudson — Consulting WorX</p>
${fillPlaceholders(form.html) || '<p style="color:#999">Your proposal HTML preview renders here…</p>'}
</body></html>`;

export const Proposals = ({ token }) => {
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState([]);
    const [clientId, setClientId] = useState("manual");
    const [form, setForm] = useState({ title: "", client_name: "", client_email: "", html: "" });
    const [busy, setBusy] = useState(false);

    const load = () => adminProposals(token).then(setItems).catch(() => {});
    useEffect(() => {
        load();
        adminClients(token).then(setClients).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const linked = clientId !== "manual" ? clients.find((c) => c.client_id === clientId) : null;

    const create = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await adminCreateProposal(token, {
                ...form,
                client_id: linked ? linked.client_id : "",
            });
            toast.success(linked ? `Proposal saved and linked to ${linked.name}.` : "Proposal saved.");
            setForm({ title: "", client_name: "", client_email: "", html: "" });
            setClientId("manual");
            load();
        } catch {
            toast.error("Could not save proposal.");
        } finally {
            setBusy(false);
        }
    };

    const send = async (p) => {
        if (!window.confirm(`Email this proposal to ${p.client_email}?`)) return;
        try {
            await adminSendProposal(token, p.proposal_id);
            toast.success(`Proposal sent to ${p.client_email}.`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Send failed.");
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete proposal "${p.title}"?`)) return;
        await adminDeleteProposal(token, p.proposal_id).catch(() => {});
        load();
    };

    const link = async (p, cid) => {
        if (!cid) return;
        try {
            await adminLinkProposal(token, p.proposal_id, cid);
            toast.success("Proposal linked to client.");
            load();
        } catch {
            toast.error("Could not link proposal.");
        }
    };

    return (
        <section className="border border-ink/10 bg-white" data-testid="admin-proposals-panel">
            <div className="px-6 py-4 border-b border-ink/10">
                <h2 className="font-display font-medium text-xl">Proposal builder</h2>
                <p className="text-xs text-ink/50 mt-1">
                    Write or paste proposal HTML — it renders with your branding as a clean downloadable PDF, or goes
                    straight to the client by email.
                </p>
            </div>
            <form onSubmit={create} data-testid="proposal-form" className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label htmlFor="p-title">Title *</Label>
                        <Input id="p-title" data-testid="proposal-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="Website Build — Client Name" className="mt-1.5 rounded-none" />
                    </div>
                    <div>
                        <Label>Link a client</Label>
                        <Select value={clientId} onValueChange={setClientId}>
                            <SelectTrigger data-testid="proposal-client-select" className="mt-1.5 rounded-none">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="manual" data-testid="proposal-client-manual">Enter manually…</SelectItem>
                                {clients.map((c) => (
                                    <SelectItem key={c.client_id} value={c.client_id} data-testid={`proposal-client-opt-${c.client_id.slice(0, 8)}`}>
                                        {c.name}{c.company ? ` — ${c.company}` : ""}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                {linked ? (
                    <p className="text-sm text-ink/70 border border-ink/10 bg-paper px-3.5 py-2.5 flex items-center justify-between" data-testid="proposal-linked-client">
                        <span>
                            Linked to <strong>{linked.name}</strong>
                            {linked.email ? ` · ${linked.email}` : " · no email on file"}
                        </span>
                        <button type="button" onClick={() => setClientId("manual")} className="text-ink/40 hover:text-worx" title="Unlink">
                            <X size={14} />
                        </button>
                    </p>
                ) : (
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="p-client">Client name *</Label>
                            <Input id="p-client" data-testid="proposal-client-input" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} required className="mt-1.5 rounded-none" />
                        </div>
                        <div>
                            <Label htmlFor="p-email">Client email (needed to send)</Label>
                            <Input id="p-email" data-testid="proposal-email-input" type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} className="mt-1.5 rounded-none" />
                        </div>
                    </div>
                )}
                <div>
                    <Label htmlFor="p-html">Proposal HTML *</Label>
                    <Textarea id="p-html" data-testid="proposal-html-input" value={form.html} onChange={(e) => setForm({ ...form, html: e.target.value })} required rows={7} placeholder="<h2>Scope</h2><p>…</p><ul><li>…</li></ul><p><strong>Total: $…</strong></p>" className="mt-1.5 rounded-none font-mono text-xs" />
                    <p className="mt-1.5 text-xs text-ink/40">
                        Auto-filled for you: [Your Name / Company Name] → Alexandra Hudson / Consulting WorX, and
                        [Insert Date] → today's date when the PDF is downloaded or the email is sent.
                    </p>
                </div>
                <iframe title="Proposal preview" data-testid="proposal-preview" sandbox="allow-same-origin" srcDoc={previewDoc({ ...form, client_name: linked ? linked.name : form.client_name })} className="w-full h-72 border border-ink/10 bg-white" />
                <Button data-testid="proposal-save-btn" type="submit" disabled={busy} className="bg-ink hover:bg-worx text-paper rounded-none">
                    {busy ? <Loader2 className="animate-spin mr-2" size={16} /> : <Plus className="mr-2" size={16} />}
                    Save proposal
                </Button>
            </form>
            {items.length > 0 && (
                <ul data-testid="proposals-list" className="divide-y divide-ink/10 border-t border-ink/10">
                    {items.map((p) => (
                        <li key={p.proposal_id} className="px-6 py-3.5 flex items-center justify-between gap-3" data-testid={`proposal-${p.proposal_id.slice(0, 8)}`}>
                            <div className="min-w-0">
                                <p className="font-medium text-sm truncate">{p.title}</p>
                                <p className="text-xs text-ink/50">
                                    {p.client_name} · {new Date(p.created_at).toLocaleDateString()} ·{" "}
                                    <span className={p.status === "sent" ? "text-worx font-medium" : ""}>{p.status}</span>
                                </p>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                                {!p.client_id && clients.length > 0 && (
                                    <select
                                        data-testid={`proposal-link-${p.proposal_id.slice(0, 8)}`}
                                        defaultValue=""
                                        onChange={(e) => link(p, e.target.value)}
                                        className="text-xs border border-worx/40 text-worx px-2 py-1.5 bg-white cursor-pointer"
                                        title="Link this proposal to a client"
                                    >
                                        <option value="" disabled>Link client…</option>
                                        {clients.map((c) => (
                                            <option key={c.client_id} value={c.client_id}>{c.name}</option>
                                        ))}
                                    </select>
                                )}
                                <button data-testid={`proposal-pdf-${p.proposal_id.slice(0, 8)}`} onClick={() => adminProposalPdf(token, p.proposal_id, p.title)} className="inline-flex items-center gap-1 text-xs border border-ink/15 px-2.5 py-1.5 hover:border-worx hover:text-worx transition-colors" title="Download PDF">
                                    <FileDown size={13} /> PDF
                                </button>
                                {p.client_email && p.status !== "sent" && (
                                    <button data-testid={`proposal-send-${p.proposal_id.slice(0, 8)}`} onClick={() => send(p)} className="inline-flex items-center gap-1 text-xs border border-ink/15 px-2.5 py-1.5 hover:border-worx hover:text-worx transition-colors" title="Email to client">
                                        <Send size={13} /> Send
                                    </button>
                                )}
                                <button data-testid={`proposal-delete-${p.proposal_id.slice(0, 8)}`} onClick={() => remove(p)} className="text-ink/40 hover:text-worx transition-colors p-1.5" title="Delete">
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
};
